import { useState, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useLiveQuery } from "dexie-react-hooks";
import { db } from "../db";
import Sidebar from "@/components/Sidebar";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { ArrowLeft, User, Calendar, DollarSign, Clock, Plus, Trash2, FileText, CalendarClock, RotateCcw } from "lucide-react";
import AppointmentNotes from "@/components/AppointmentNotes";
import SyncIndicator from "@/components/SyncIndicator";
import NotificationStatusBadge from "@/components/NotificationStatusBadge";
import { syncService, buildAppointmentRollbackData } from "../services/SyncService";
import { toast } from "sonner";
import { formatRupees, toPaise } from "../utils/currency";
import { useUserRole } from "../hooks/useUserRole";
import { getAppointmentAccess } from "../utils/accessPolicy";
import PropTypes from 'prop-types';
import { getDisplayAge } from "../utils/patient";

export default function AppointmentDetail({ token, onLogout }) {
    const { isReceptionistOnly, memberships } = useUserRole();
    const { appointmentId } = useParams();
    const navigate = useNavigate();
    const [showChargeDialog, setShowChargeDialog] = useState(false);
    const [chargeAmount, setChargeAmount] = useState("");
    const [showCancellationDialog, setShowCancellationDialog] = useState(false);
    const [cancellationReason, setCancellationReason] = useState("");
    const [showReopenDialog, setShowReopenDialog] = useState(false);

    // Edit fee state
    const [showEditFeeDialog, setShowEditFeeDialog] = useState(false);
    const [editFeeAmount, setEditFeeAmount] = useState("");
    const [editFeeNotes, setEditFeeNotes] = useState("");

    // Reschedule state
    const [showRescheduleDialog, setShowRescheduleDialog] = useState(false);
    const [rescheduleDateTime, setRescheduleDateTime] = useState("");
    const [rescheduleDuration, setRescheduleDuration] = useState("");

    const data = useLiveQuery(async () => {
        if (!appointmentId) return null;
        console.log("AppointmentDetail querying for:", appointmentId);
        const appointment = await db.appointments.get(appointmentId);
        console.log("AppointmentDetail query result:", appointment);
        if (!appointment) return null;

        // If patient_id is missing (rare glitch), return null for patient to handle gracefully
        if (!appointment.patient_id) return { appointment, patient: null, facility_charge: null, referralDoctor: null, treatingDoctor: null, clinic: null };
        const patient = await db.patients.get(appointment.patient_id);

        // Get facility_charge for this appointment
        const facility_charge = await db.facility_charges.where('appointment_id').equals(appointmentId).first();

        // Get referral doctor info (historical from patient)
        let referralDoctor = null;
        if (patient?.referral_doctor_id) {
            referralDoctor = await db.users.get(patient.referral_doctor_id);
        }

        // Get treating doctor info
        let treatingDoctor = null;
        if (appointment.doctor_id) {
            treatingDoctor = await db.users.get(appointment.doctor_id);
        }

        // Get clinic info and the receiver of the charge (Clinic Owner)
        let clinic = null;
        let receiverDoctor = null;
        if (appointment.clinic_id) {
            clinic = await db.clinics.get(appointment.clinic_id);
            if (clinic?.owner_doctor_id) {
                receiverDoctor = await db.users.get(clinic.owner_doctor_id);
            }
        }

        return { appointment, patient, facility_charge, referralDoctor, treatingDoctor, clinic, receiverDoctor };
    }, [appointmentId]);

    // suggestedCommission returns paise
    const suggestedCommission = useMemo(() => {
        if (!data?.patient?.rule_type || !data?.patient?.fee_value || !data?.appointment) return null;
        if (data.patient.rule_type === 'percent') {
            // fee_amount is paise, fee_value is whole percentage
            return data.appointment.fee_amount * data.patient.fee_value / 100;
        }
        // flat, already paise
        return data.patient.fee_value;
    }, [data]);

    const updateStatus = async (newStatus, reason = null) => {
        const { canEdit: currentCanEdit } = getAppointmentAccess(
            data?.appointment,
            localStorage.getItem("user_id"),
            memberships
        );
        if (!currentCanEdit) {
            toast.error("You do not have permission to edit this appointment");
            return;
        }

        try {
            const timestamp = new Date().toISOString();
            const updateData = {
                status: newStatus,
                updated_at: timestamp
            };

            // Include cancellation reason if provided
            if (newStatus === 'cancelled' && reason) {
                updateData.cancellation_reason = reason;
            }

            const existingCharge = facility_charge || await db.facility_charges.where('appointment_id').equals(appointmentId).first();
            const rollbackData = buildAppointmentRollbackData(
                data.appointment,
                ["status", "cancellation_reason"],
                existingCharge
            );

            await db.appointments.update(appointmentId, updateData);

            // If appointment is cancelled, delete the corresponding facility_charge
            if (newStatus === 'cancelled') {
                await syncService.addToQueue('update', 'appointment', {
                    id: appointmentId,
                    ...updateData
                }, { rollbackData, processImmediately: false });

                if (existingCharge) {
                    await db.facility_charges.delete(existingCharge.id);
                    await syncService.addToQueue('delete', 'facility_charge', { id: existingCharge.id }, { processImmediately: false });
                }
                syncService.processQueue();
            } else {
                await syncService.addToQueue('update', 'appointment', {
                    id: appointmentId,
                    ...updateData
                }, { rollbackData });
            }

            toast.success(`Appointment marked as ${newStatus}`);
        } catch (error) {
            console.error(error);
            toast.error("Failed to update status");
        }
    };

    const handleCancelAppointment = () => {
        setShowCancellationDialog(true);
    };

    const handleConfirmCancellation = async (skipReason = false) => {
        const reason = skipReason ? null : cancellationReason.trim() || null;
        await updateStatus('cancelled', reason);
        setShowCancellationDialog(false);
        setCancellationReason("");
    };

    const handleAddCharge = async (e) => {
        e.preventDefault();
        const { canEdit: currentCanEdit } = getAppointmentAccess(
            data?.appointment,
            localStorage.getItem("user_id"),
            memberships
        );
        if (!currentCanEdit) {
            toast.error("You do not have permission to edit this appointment");
            return;
        }
        try {
            let amount = parseInt(chargeAmount);

            // Use suggested if no amount provided
            if (!amount && suggestedCommission) {
                amount = suggestedCommission; // already in paise
            } else if (amount) {
                // User entered rupees, convert to paise
                amount = toPaise(chargeAmount);
            }

            if (!amount || amount <= 0) {
                toast.error("Please enter a valid facility_charge amount");
                return;
            }

            const newCharge = {
                id: `comm_${appointmentId}`,
                appointment_id: appointmentId,
                associated_doctor_id: appointment.doctor_id, // The treating doctor being charged
                clinic_id: appointment.clinic_id || null,
                charge_amount: amount,
                paid_status: 'pending',
                created_at: new Date().toISOString(),
                updated_at: new Date().toISOString(),
                _synced: false
            };

            await db.facility_charges.add(newCharge);
            await syncService.addToQueue('create', 'facility_charge', newCharge);

            toast.success("Facility charge created successfully!");
            setShowChargeDialog(false);
            setChargeAmount("");
        } catch (error) {
            console.error(error);
            toast.error("Failed to create facility_charge");
        }
    };

    const handleDeleteCommission = async () => {
        if (!facility_charge) return;
        const { canEdit: currentCanEdit } = getAppointmentAccess(
            data?.appointment,
            localStorage.getItem("user_id"),
            memberships
        );
        if (!currentCanEdit) {
            toast.error("You do not have permission to edit this appointment");
            return;
        }

        if (!window.confirm("Are you sure you want to delete this facility_charge record?")) {
            return;
        }

        try {
            await db.facility_charges.delete(facility_charge.id);
            await syncService.addToQueue('delete', 'facility_charge', { id: facility_charge.id });
            toast.success("Facility charge deleted successfully!");
        } catch (error) {
            console.error(error);
            toast.error("Failed to delete facility_charge");
        }
    };

    const handleOpenReschedule = () => {
        if (data?.appointment) {
            // Format datetime for input
            const dt = new Date(data.appointment.date_time);
            const year = dt.getFullYear();
            const month = String(dt.getMonth() + 1).padStart(2, '0');
            const day = String(dt.getDate()).padStart(2, '0');
            const hours = String(dt.getHours()).padStart(2, '0');
            const mins = String(dt.getMinutes()).padStart(2, '0');
            setRescheduleDateTime(`${year}-${month}-${day}T${hours}:${mins}`);
            setRescheduleDuration(data.appointment.duration_minutes || 30);
            setShowRescheduleDialog(true);
        }
    };

    // Open edit fee dialog with current fee pre-filled
    const handleOpenEditFee = () => {
        const { canEdit: currentCanEdit } = getAppointmentAccess(
            data?.appointment,
            localStorage.getItem("user_id"),
            memberships
        );
        if (!currentCanEdit) {
            toast.error("You do not have permission to edit this appointment");
            return;
        }

        if (data?.appointment) {
            // Convert paise to whole rupees for display
            setEditFeeAmount(String(Math.round((data.appointment.fee_amount || 0) / 100)));
            setEditFeeNotes("");
            setShowEditFeeDialog(true);
        }
    };

    // Handle edit fee submit
    const handleEditFee = async (e) => {
        e.preventDefault();
        const { canEdit: currentCanEdit } = getAppointmentAccess(
            data?.appointment,
            localStorage.getItem("user_id"),
            memberships
        );
        if (!currentCanEdit) {
            toast.error("You do not have permission to edit this appointment");
            return;
        }

        const rupees = parseInt(editFeeAmount, 10);
        if (isNaN(rupees) || rupees < 0) {
            toast.error("Please enter a valid fee amount");
            return;
        }
        try {
            const newFeeInPaise = rupees * 100;
            const timestamp = new Date().toISOString();
            const updateData = {
                fee_amount: newFeeInPaise,
                updated_at: timestamp,
                _synced: false,
            };
            if (editFeeNotes.trim()) {
                updateData.notes = editFeeNotes.trim();
            }

            // Recalculate pending facility charge locally if one exists
            const existingCharge = await db.facility_charges
                .where('appointment_id').equals(appointmentId).first();

            const rollbackData = buildAppointmentRollbackData(
                data.appointment,
                ["fee_amount", "notes"],
                existingCharge
            );

            // Optimistic local update
            await db.appointments.update(appointmentId, updateData);

            if (existingCharge && existingCharge.paid_status === 'pending') {
                // Attempt local recalc using the fee_agreement if available
                let newChargeAmount = existingCharge.charge_amount;
                if (existingCharge.fee_agreement_id) {
                    const agreement = await db.fee_agreements?.get(existingCharge.fee_agreement_id);
                    if (agreement) {
                        if (agreement.rule_type === 'percent') {
                            newChargeAmount = Math.round(newFeeInPaise * (agreement.fee_value / 100));
                        } else if (agreement.rule_type === 'flat') {
                            newChargeAmount = agreement.fee_value;
                        }
                    }
                }
                await db.facility_charges.update(existingCharge.id, {
                    charge_amount: newChargeAmount,
                    updated_at: timestamp,
                    // Do NOT set _synced: false here — there is no sync queue entry for facility charges.
                    // The backend recalculates the charge as a side-effect of the appointment update
                    // and pushes the authoritative value via socket (facility_charge_updated event).
                });
            }

            // Queue sync — backend will perform authoritative recalc of facility charge
            await syncService.addToQueue('update', 'appointment', {
                id: appointmentId,
                fee_amount: newFeeInPaise,
                ...(editFeeNotes.trim() ? { notes: editFeeNotes.trim() } : {}),
                updated_at: timestamp,
            }, { rollbackData });

            toast.success("Fee updated successfully");
            setShowEditFeeDialog(false);
            setEditFeeAmount("");
            setEditFeeNotes("");
        } catch (error) {
            console.error(error);
            toast.error("Failed to update fee");
        }
    };

    // Handle reschedule submit
    const handleReschedule = async (e) => {
        e.preventDefault();
        const { canEdit: currentCanEdit } = getAppointmentAccess(
            data?.appointment,
            localStorage.getItem("user_id"),
            memberships
        );
        if (!currentCanEdit) {
            toast.error("You do not have permission to edit this appointment");
            return;
        }

        try {
            const timestamp = new Date().toISOString();
            const updateData = {
                date_time: rescheduleDateTime,
                duration_minutes: parseInt(rescheduleDuration) || 30,
                updated_at: timestamp
            };

            const rollbackData = buildAppointmentRollbackData(
                data.appointment,
                ["date_time", "duration_minutes"]
            );

            await db.appointments.update(appointmentId, updateData);

            await syncService.addToQueue('update', 'appointment', {
                id: appointmentId,
                date_time: rescheduleDateTime,
                duration_minutes: parseInt(rescheduleDuration) || 30,
                updated_at: timestamp
            }, { rollbackData });

            toast.success("Appointment rescheduled successfully");
            setShowRescheduleDialog(false);
        } catch (error) {
            console.error(error);
            toast.error("Failed to reschedule appointment");
        }
    };

    if (!data) {
        return (
            <div className="page-container">
                <Sidebar active="appointments" onLogout={onLogout} />
                <div className="main-content">
                    <div className="header">
                        <h1 style={{ textAlign: 'center', width: '100%', color: '#64748b' }} data-testid="appointment-loading">Loading...</h1>
                    </div>
                </div>
            </div>
        );
    }

    const { appointment, patient, facility_charge, referralDoctor, treatingDoctor, clinic, receiverDoctor } = data;

    const { canEdit, canViewFee } = getAppointmentAccess(
        appointment,
        localStorage.getItem("user_id"),
        memberships
    );

    const canAddCommission = canEdit && !isReceptionistOnly && !facility_charge &&
        patient?.referral_doctor_id &&
        (appointment.status === 'completed' || appointment.status === 'cancelled');

    // Can generate invoice if completed and no invoice yet
    const canGenerateInvoice = canEdit && appointment.status === 'completed' && !appointment.invoice_id;

    // Reopen guards
    const isReopenBlocked = Boolean(appointment?.invoice_id || facility_charge?.paid_status === 'paid');
    const reopenBlockReason = appointment?.invoice_id
        ? "An invoice has been generated for this appointment."
        : facility_charge?.paid_status === 'paid'
        ? "The facility charge for this appointment has been paid."
        : null;

    return (
        <div className="page-container" data-testid="appointment-detail-page">
            <Sidebar active="appointments" onLogout={onLogout} />
            <div className="main-content">
                <div className="header" style={{ marginBottom: '1.5rem' }}>
                    <Button
                        variant="ghost"
                        onClick={() => navigate(-1)}
                        style={{ display: 'flex', alignItems: 'center' }}
                    >
                        <ArrowLeft size={20} style={{ marginRight: '0.5rem' }} />
                        Back
                    </Button>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <h1 style={{ fontSize: '1.5rem', fontWeight: '700' }}>Appointment Details</h1>
                        <SyncIndicator show={!appointment._synced} />
                        <NotificationStatusBadge token={token} entityType="appointment" entityId={appointmentId} compact />
                    </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '1.5rem' }}>
                    <div className="space-y-6">
                        <div className="card">
                            <h2 style={{ fontSize: '1.125rem', fontWeight: '600', marginBottom: '1.25rem', display: 'flex', alignItems: 'center', gap: '0.5rem', color: '#0ea5e9' }}>
                                <Calendar size={18} />
                                Session Information
                            </h2>
                            <div className="space-y-4">
                                <div>
                                    <div style={{ color: '#64748b', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.25rem' }}>Date & Time</div>
                                    <div style={{ fontWeight: '600', fontSize: '1.125rem' }}>
                                        {appointment.date_time ? new Date(appointment.date_time).toLocaleString() : <span className="text-muted-foreground italic text-sm">Updating...</span>}
                                    </div>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', gap: '1rem' }}>
                                    <div>
                                        <div style={{ color: '#64748b', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.25rem' }}>Status</div>
                                        <span className={`badge badge-${appointment.status}`}>{appointment.status}</span>
                                    </div>
                                    <div style={{ textAlign: 'center' }}>
                                        <div style={{ color: '#64748b', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.25rem' }}>Duration</div>
                                        <div style={{ fontWeight: '600', display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                                            <Clock size={14} className="text-slate-500" />
                                            {appointment.duration_minutes || 30} min
                                        </div>
                                    </div>
                                    {canViewFee && (
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
                                            <div>
                                                <div style={{ color: '#64748b', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.25rem' }}>Fee</div>
                                                <div style={{ fontWeight: '700', fontSize: '1.25rem', color: '#0ea5e9' }}>{formatRupees(appointment.fee_amount || 0)}</div>
                                            </div>
                                            {/* Edit Fee button — available to authorized editors; guards are data-driven */}
                                            {canEdit && !appointment.invoice_id && appointment.status !== 'cancelled' && facility_charge?.paid_status !== 'paid' && (
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={handleOpenEditFee}
                                                    style={{ fontSize: '0.75rem', color: '#0ea5e9', padding: '0.25rem 0.5rem', height: 'auto' }}
                                                >
                                                    Edit
                                                </Button>
                                            )}
                                        </div>
                                    )}
                                </div>
                                {/* Doctor & Clinic Info */}
                                <div style={{ display: 'flex', gap: '1.5rem', flexWrap: 'wrap', padding: '0.75rem', background: '#f8fafc', borderRadius: '8px' }}>
                                    <div>
                                        <div style={{ color: '#64748b', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.25rem' }}>Doctor</div>
                                        <div style={{ fontWeight: '600', fontSize: '0.9rem' }}>
                                            {treatingDoctor ? (treatingDoctor.name?.startsWith('Dr.') ? treatingDoctor.name : `Dr. ${treatingDoctor.name}`) : 'Unknown'}
                                        </div>
                                    </div>
                                    <div>
                                        <div style={{ color: '#64748b', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.25rem' }}>Clinic</div>
                                        <div style={{ fontWeight: '600', fontSize: '0.9rem' }}>
                                            {clinic ? clinic.name : 'Personal Practice'}
                                        </div>
                                    </div>
                                </div>
                                {appointment.notes && (
                                    <div style={{ padding: '0.75rem', background: '#f8fafc', borderRadius: '8px', borderLeft: '3px solid #cbd5e1' }}>
                                        <div style={{ color: '#64748b', fontSize: '0.75rem', marginBottom: '0.25rem' }}>Initial Notes</div>
                                        <div style={{ fontSize: '0.875rem' }}>{appointment.notes}</div>
                                    </div>
                                )}
                                {appointment.status === "cancelled" && appointment.cancellation_reason && (
                                    <div style={{ padding: '0.75rem', background: '#fef2f2', borderRadius: '8px', borderLeft: '3px solid #fca5a5' }}>
                                        <div style={{ color: '#dc2626', fontSize: '0.75rem', marginBottom: '0.25rem', fontWeight: '600' }}>Cancellation Reason</div>
                                        <div style={{ fontSize: '0.875rem', color: '#7f1d1d' }}>{appointment.cancellation_reason}</div>
                                    </div>
                                )}
                                {canEdit && appointment.status === "pending" && (
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', paddingTop: '0.5rem' }}>
                                        <div style={{ display: 'flex', gap: '0.75rem' }}>
                                            <Button
                                                className="flex-1 h-10 bg-emerald-600 hover:bg-emerald-700 text-white transition-colors"
                                                onClick={() => updateStatus("scheduled")}
                                            >
                                                Accept Request
                                            </Button>
                                            <Button
                                                variant="outline"
                                                className="flex-1 h-10 text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700"
                                                onClick={handleCancelAppointment}
                                            >
                                                Reject Request
                                            </Button>
                                        </div>
                                    </div>
                                )}
                                {canEdit && appointment.status === "scheduled" && (
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', paddingTop: '0.5rem' }}>
                                        <div style={{ display: 'flex', gap: '0.75rem' }}>
                                            <Button
                                                variant="outline"
                                                className="flex-1 h-10 text-green-600 border-green-200 hover:bg-green-50 hover:text-green-700"
                                                onClick={() => updateStatus("completed")}
                                            >
                                                Mark Complete
                                            </Button>
                                            <Button
                                                variant="outline"
                                                className="flex-1 h-10 text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700"
                                                onClick={handleCancelAppointment}
                                            >
                                                Cancel
                                            </Button>
                                        </div>
                                        <Button
                                            variant="outline"
                                            className="w-full h-10 text-blue-600 border-blue-200 hover:bg-blue-50 hover:text-blue-700"
                                            onClick={handleOpenReschedule}
                                        >
                                            <CalendarClock size={16} className="mr-2" />
                                            Reschedule
                                        </Button>
                                    </div>
                                )}
                                {canEdit && appointment.status === "completed" && (
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', paddingTop: '0.5rem' }}>
                                        <Button
                                            variant="outline"
                                            disabled={isReopenBlocked}
                                            title={reopenBlockReason || "Reopen appointment to Scheduled status"}
                                            className="w-full h-10 text-amber-600 border-amber-200 hover:bg-amber-50 hover:text-amber-700 disabled:opacity-50 disabled:cursor-not-allowed"
                                            onClick={() => setShowReopenDialog(true)}
                                            data-testid="reopen-appointment-button"
                                        >
                                            <RotateCcw size={16} className="mr-2" />
                                            Reopen Appointment
                                        </Button>
                                        {reopenBlockReason && (
                                            <div style={{ fontSize: '0.75rem', color: '#94a3b8', fontStyle: 'italic', textAlign: 'center' }}>
                                                {reopenBlockReason}
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Facility Charge Section */}
                        {!isReceptionistOnly && canViewFee && (
                            <div className="card">
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                                    <h2 style={{ fontSize: '1.125rem', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '0.5rem', color: '#10b981' }}>
                                        <DollarSign size={18} />
                                        Facility Charge
                                    </h2>
                                    {canAddCommission && (
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={() => setShowChargeDialog(true)}
                                            style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}
                                        >
                                            <Plus size={16} />
                                            Add Charge
                                        </Button>
                                    )}
                                </div>

                                {facility_charge ? (
                                    <div
                                        onClick={() => navigate(`/facility-charges/${facility_charge.id}`)}
                                        style={{ padding: '1rem', background: '#f0fdf4', borderRadius: '8px', border: '1px solid #86efac', cursor: 'pointer' }}
                                    >
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                                <span style={{ fontWeight: '700', fontSize: '1.25rem', color: '#16a34a' }}>
                                                    {formatRupees(facility_charge.charge_amount)}
                                                </span>
                                                <SyncIndicator show={facility_charge._synced === false} />
                                            </div>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                                <span className={`badge badge-${facility_charge.paid_status}`}>{facility_charge.paid_status}</span>
                                                {facility_charge.paid_status === 'pending' && (
                                                    <Button
                                                        variant="ghost"
                                                        size="sm"
                                                        onClick={handleDeleteCommission}
                                                        className="h-7 w-7 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                                                    >
                                                        <Trash2 size={14} />
                                                    </Button>
                                                )}
                                            </div>
                                        </div>
                                        {(clinic || receiverDoctor) && (
                                            <div style={{ fontSize: '0.875rem', color: '#64748b' }}>
                                                To: {clinic?.name || receiverDoctor?.name}
                                            </div>
                                        )}
                                        {facility_charge.payment_date && (
                                            <div style={{ fontSize: '0.75rem', color: '#64748b', marginTop: '0.25rem' }}>
                                                Paid on: {new Date(facility_charge.payment_date).toLocaleDateString()}
                                            </div>
                                        )}
                                    </div>
                                ) : receiverDoctor ? (
                                    <div style={{ textAlign: 'center', padding: '1.5rem', color: '#94a3b8', background: '#f8fafc', borderRadius: '8px' }}>
                                        <div style={{ marginBottom: '0.5rem' }}>No charge recorded</div>
                                        <div style={{ fontSize: '0.75rem' }}>
                                            Payable to: {receiverDoctor.name}
                                        </div>
                                    </div>
                                ) : (
                                    <div style={{ textAlign: 'center', padding: '1.5rem', color: '#94a3b8' }}>
                                        No facility charge applicable
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Invoice Section */}
                        {canViewFee && (
                            <div className="card">
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                                    <h2 style={{ fontSize: '1.125rem', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '0.5rem', color: '#0ea5e9' }}>
                                        <FileText size={18} />
                                        Invoice
                                    </h2>
                                </div>
                                {appointment.invoice_id ? (
                                    <div
                                        onClick={() => navigate(`/invoices/${appointment.invoice_id}`)}
                                        style={{ padding: '1rem', background: '#eff6ff', borderRadius: '8px', border: '1px solid #93c5fd', cursor: 'pointer' }}
                                    >
                                        <div style={{ fontWeight: '600', color: '#2563eb' }}>Invoice Generated</div>
                                        <div style={{ fontSize: '0.875rem', color: '#64748b' }}>Click to view invoice details</div>
                                    </div>
                                ) : canGenerateInvoice ? (
                                    <Button
                                        onClick={() => navigate(`/invoices/new?patient=${patient?.id}&appointment=${appointmentId}`)}
                                        style={{ width: '100%', background: 'linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)' }}
                                        data-testid="generate-invoice-button"
                                    >
                                        <FileText size={16} style={{ marginRight: '0.5rem' }} />
                                        Generate Invoice
                                    </Button>
                                ) : (
                                    <div style={{ textAlign: 'center', padding: '1.5rem', color: '#94a3b8', background: '#f8fafc', borderRadius: '8px' }}>
                                        {appointment.status === 'scheduled' ? 'Complete appointment to generate invoice' : 'Invoice not available for cancelled appointments'}
                                    </div>
                                )}
                            </div>
                        )}

                        <div className="card" onClick={() => patient && navigate(`/patients/${patient.id}`)} style={{ cursor: patient ? 'pointer' : 'default' }}>
                            <h2 style={{ fontSize: '1.125rem', fontWeight: '600', marginBottom: '1.25rem', display: 'flex', alignItems: 'center', gap: '0.5rem', color: '#64748b' }}>
                                <User size={18} />
                                Patient Context
                            </h2>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                                <div style={{ width: '48px', height: '48px', background: '#dbeafe', borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    <User size={24} color="#1e40af" />
                                </div>
                                <div>
                                    <div style={{ fontWeight: '700', fontSize: '1.125rem' }}>{patient ? patient.name : 'Unknown Patient'}</div>
                                    <div style={{ fontSize: '0.875rem', color: '#64748b' }}>{patient ? `${getDisplayAge(patient) ? getDisplayAge(patient) + 'y' : '—'} • ${patient.gender}` : 'Patient information unavailable'}</div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {!isReceptionistOnly && (
                        <div className="card">
                            <h2 style={{ fontSize: '1.125rem', fontWeight: '600', marginBottom: '1rem' }}>Clinical Notes & Media</h2>
                            <AppointmentNotes appointmentId={appointmentId} userId={localStorage.getItem('user_id')} token={token} />
                        </div>
                    )}
                </div>
            </div>

            {/* Add Facility Charge Dialog */}
            <Dialog open={showChargeDialog} onOpenChange={setShowChargeDialog}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Add Facility Charge</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleAddCharge}>
                        <div style={{ marginBottom: '1rem' }}>
                            <div style={{ marginBottom: '1rem', padding: '0.75rem', background: '#f0fdf4', borderRadius: '8px' }}>
                                <div style={{ fontSize: '0.875rem', color: '#64748b' }}>Associated Doctor</div>
                                <div style={{ fontWeight: '600' }}>{referralDoctor?.name}</div>
                            </div>
                            <Label htmlFor="charge_amount">Facility Charge Amount (₹) - Whole rupees</Label>
                            <Input
                                id="charge_amount"
                                type="number"
                                step="1"
                                min="0"
                                placeholder={suggestedCommission ? `Suggested: ${formatRupees(suggestedCommission)}` : "Amount in Rupees"}
                                value={chargeAmount}
                                onChange={(e) => setChargeAmount(e.target.value)}
                            />
                            {suggestedCommission && !chargeAmount && (
                                <div style={{ fontSize: '0.75rem', color: '#64748b', marginTop: '0.25rem' }}>
                                    Leave empty to use suggested amount based on patient&apos;s settings
                                </div>
                            )}
                        </div>

                        <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
                            <Button type="button" variant="outline" onClick={() => setShowChargeDialog(false)}>
                                Cancel
                            </Button>
                            <Button
                                type="submit"
                                style={{ background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)' }}
                            >
                                Add Charge
                            </Button>
                        </div>
                    </form>
                </DialogContent>
            </Dialog>

            {/* Cancellation Reason Dialog */}
            <Dialog open={showCancellationDialog} onOpenChange={setShowCancellationDialog}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Cancel Appointment</DialogTitle>
                        <DialogDescription>
                            You can optionally provide a reason for cancelling this appointment.
                        </DialogDescription>
                    </DialogHeader>
                    <div style={{ marginBottom: '1rem' }}>
                        <Label htmlFor="cancellation_reason">Cancellation Reason (Optional)</Label>
                        <Textarea
                            id="cancellation_reason"
                            placeholder="Enter reason for cancellation..."
                            value={cancellationReason}
                            onChange={(e) => setCancellationReason(e.target.value)}
                            rows={3}
                            style={{ marginTop: '0.5rem' }}
                        />
                    </div>
                    <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
                        <Button
                            type="button"
                            variant="outline"
                            onClick={() => handleConfirmCancellation(true)}
                        >
                            Skip
                        </Button>
                        <Button
                            type="button"
                            onClick={() => handleConfirmCancellation(false)}
                            className="bg-red-600 hover:bg-red-700 text-white"
                        >
                            Cancel Appointment
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Edit Fee Dialog */}
            <Dialog open={showEditFeeDialog} onOpenChange={setShowEditFeeDialog}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Edit Appointment Fee</DialogTitle>
                        <DialogDescription>
                            Update the consultation fee for this appointment. Enter the amount in whole rupees.
                        </DialogDescription>
                    </DialogHeader>
                    <form onSubmit={handleEditFee}>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', marginBottom: '1rem' }}>
                            <div>
                                <Label htmlFor="edit_fee_amount">New Fee (₹)</Label>
                                <Input
                                    id="edit_fee_amount"
                                    type="number"
                                    step="1"
                                    min="0"
                                    value={editFeeAmount}
                                    onChange={(e) => setEditFeeAmount(e.target.value)}
                                    required
                                    style={{ marginTop: '0.25rem' }}
                                />
                            </div>
                            <div>
                                <Label htmlFor="edit_fee_notes">Reason for change (optional)</Label>
                                <Textarea
                                    id="edit_fee_notes"
                                    placeholder="e.g. Additional procedure performed, discount applied..."
                                    value={editFeeNotes}
                                    onChange={(e) => setEditFeeNotes(e.target.value)}
                                    rows={2}
                                    style={{ marginTop: '0.25rem' }}
                                />
                            </div>
                            {facility_charge?.paid_status === 'pending' && (
                                <div style={{ padding: '0.75rem', background: '#fffbeb', borderRadius: '8px', borderLeft: '3px solid #f59e0b', fontSize: '0.8125rem', color: '#92400e' }}>
                                    A pending facility charge exists for this appointment. It will be recalculated automatically based on the new fee.
                                </div>
                            )}
                        </div>
                        <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
                            <Button type="button" variant="outline" onClick={() => setShowEditFeeDialog(false)}>
                                Cancel
                            </Button>
                            <Button
                                type="submit"
                                style={{ background: 'linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)' }}
                            >
                                Save Fee
                            </Button>
                        </div>
                    </form>
                </DialogContent>
            </Dialog>

            {/* Reschedule Dialog */}
            <Dialog open={showRescheduleDialog} onOpenChange={setShowRescheduleDialog}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Reschedule Appointment</DialogTitle>
                        <DialogDescription>
                            Select a new date and time for this appointment.
                        </DialogDescription>
                    </DialogHeader>
                    <form onSubmit={handleReschedule}>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', marginBottom: '1rem' }}>
                            <div>
                                <Label htmlFor="reschedule_datetime">New Date & Time</Label>
                                <Input
                                    id="reschedule_datetime"
                                    type="datetime-local"
                                    value={rescheduleDateTime}
                                    onChange={(e) => setRescheduleDateTime(e.target.value)}
                                    required
                                    style={{ marginTop: '0.25rem' }}
                                />
                            </div>
                            <div>
                                <Label htmlFor="reschedule_duration">Duration (minutes)</Label>
                                <div className="flex gap-2 items-center mt-1">
                                    <Input
                                        id="reschedule_duration"
                                        type="number"
                                        step="1"
                                        min="15"
                                        max="480"
                                        value={rescheduleDuration}
                                        onChange={(e) => setRescheduleDuration(e.target.value)}
                                        className="w-24"
                                    />
                                    <div className="flex gap-1">
                                        {[15, 30, 45, 60].map((mins) => (
                                            <Button
                                                key={mins}
                                                type="button"
                                                variant={parseInt(rescheduleDuration) === mins ? "default" : "outline"}
                                                size="sm"
                                                className="h-9 px-2 text-xs"
                                                onClick={() => setRescheduleDuration(mins)}
                                            >
                                                {mins === 60 ? '1h' : `${mins}m`}
                                            </Button>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
                            <Button type="button" variant="outline" onClick={() => setShowRescheduleDialog(false)}>
                                Cancel
                            </Button>
                            <Button
                                type="submit"
                                style={{ background: 'linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)' }}
                            >
                                Reschedule
                            </Button>
                        </div>
                    </form>
                </DialogContent>
            </Dialog>

            {/* Reopen Confirmation Dialog */}
            <Dialog open={showReopenDialog} onOpenChange={setShowReopenDialog}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Reopen Appointment</DialogTitle>
                        <DialogDescription>
                            Are you sure you want to reopen this completed appointment? Its status will revert to Scheduled.
                        </DialogDescription>
                    </DialogHeader>
                    <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end', marginTop: '1rem' }}>
                        <Button type="button" variant="outline" onClick={() => setShowReopenDialog(false)}>
                            Cancel
                        </Button>
                        <Button
                            type="button"
                            className="bg-amber-600 hover:bg-amber-700 text-white"
                            onClick={async () => {
                                await updateStatus("scheduled");
                                setShowReopenDialog(false);
                            }}
                        >
                            Reopen
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>
        </div >
    );
}

AppointmentDetail.propTypes = {
    token: PropTypes.string,
    onLogout: PropTypes.func
};
